import React, { useState, useEffect, useRef } from 'react';
import { Input, Switch, Select, Button, Spin, Alert, Tooltip } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import {
  CheckCircleFilled, CloseCircleFilled, EyeInvisibleOutlined, EyeOutlined,
  LoadingOutlined, SettingOutlined, SafetyCertificateOutlined, EditOutlined,
  MailOutlined, ThunderboltOutlined, CheckCircleOutlined, SearchOutlined,
  UploadOutlined, DownloadOutlined, LockOutlined, BulbOutlined, SyncOutlined,
  SafetyOutlined,
} from '@ant-design/icons';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { getUser } from '../../variables/constants';
import { toast } from 'react-toastify';
import T from './theme';
import OssanEditor from './OssanEditor';
import { avecLibelleTraduit } from '../../i18n/libelles';

// ── Protocoles ────────────────────────────────────────────────────
const PROTOCOLES = avecLibelleTraduit([
  { value: 'STARTTLS' },
  { value: 'SSL' },
  { value: 'NONE' },
], 'messagerie:config.protocoles');

// ── Helpers ────────────────────────────────────────────────────────
const FieldRow = ({ label, hint, children }) => (
  <div style={{ marginBottom: 18 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>{label}</span>
      {hint && <span style={{ fontSize: 11, color: T.textMut }}>{hint}</span>}
    </div>
    {children}
  </div>
);

const inputStyle = {
  borderRadius: 10, border: `1.5px solid ${T.border}`, height: 42, fontSize: 14,
  fontFamily: "'DM Sans', sans-serif",
};

const StatusBadge = ({ ok, label }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12,
    fontWeight: 600, padding: '4px 12px', borderRadius: 20,
    background: ok ? '#dcfce7' : '#fee2e2', color: ok ? T.green : '#dc2626',
  }}>
    {ok ? <CheckCircleFilled /> : <CloseCircleFilled />} {label}
  </span>
);

// ── Etape indicator ────────────────────────────────────────────────
const StepDot = ({ n, current, done }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
    <div style={{
      width: 32, height: 32, borderRadius: '50%', display: 'flex',
      alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700,
      transition: 'all 0.3s',
      background: done ? T.green : current ? T.ink : T.contextBg,
      color: done || current ? T.panel : T.textMut,
      border: `2px solid ${done ? T.green : current ? T.ink : T.border}`,
      boxShadow: current ? `0 0 0 4px ${T.greenSoft}` : 'none',
    }}>
      {done ? <CheckCircleFilled style={{ fontSize: 14 }} /> : n}
    </div>
  </div>
);

// Libellés des étapes : messagerie.json, config.etapes.<code>, lus au rendu.
const ETAPES = ['detection', 'serveurs', 'motDePasse', 'signature'];

// ══════════════════════════════════════════════════════════════════
//  PAGE PRINCIPALE
// ══════════════════════════════════════════════════════════════════
export default function ConfigurationMessagerie() {
  const { t } = useTranslation('messagerie');
  const user = getUser();
  const [step, setStep] = useState(0); // 0=detect 1=servers 2=password 3=signature 4=done
  const [detection, setDetection] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const [config, setConfig] = useState(null);   // config existante
  const [loading, setLoading] = useState(true);

  // Champs wizard
  const [form, setForm] = useState({
    nomAffiche: '', smtpHost: '', smtpPort: 587, smtpSecurite: 'STARTTLS',
    imapHost: '', imapPort: 993, imapSecurite: 'SSL',
    smtpUsername: '', motDePasse: '', signature: '',
  });
  const [showPwd, setShowPwd] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const signatureRef = useRef(null);

  // ── Chargement config existante ──
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axiosInstance.get('/v1/messagerie/config');
        const data = res.data?.data;
        if (data?.estConfiguree) {
          setConfig(data);
          setForm(f => ({
            ...f,
            nomAffiche: data.nomAffiche || '',
            smtpHost: data.smtpHost || '',
            smtpPort: data.smtpPort || 587,
            smtpSecurite: data.smtpSecurite || 'STARTTLS',
            imapHost: data.imapHost || '',
            imapPort: data.imapPort || 993,
            imapSecurite: data.imapSecurite || 'SSL',
            smtpUsername: data.smtpUsername || user?.email || '',
            signature: data.signature || defaultSignature(),
          }));
          setStep(4); // déjà configuré - afficher récap
        } else {
          // Lancer la détection automatique
          autoDetect();
        }
      } catch { autoDetect(); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const defaultSignature = () => {
    const nom = user ? `${user.prenoms || ''} ${user.nom || ''}`.trim() : '';
    return `<p style="margin:0;font-family:sans-serif;color:#334155;">${nom}</p>
<p style="margin:4px 0 0;font-size:12px;color:#94a3b8;">Bureau National Carte Brune CEDEAO – Togo<br>CBConnect</p>`;
  };

  const autoDetect = async () => {
    const email = user?.email;
    if (!email) return;
    setDetecting(true);
    try {
      const res = await axiosInstance.get(`/v1/messagerie/detecter?email=${encodeURIComponent(email)}`);
      const d = res.data?.data;
      if (d) {
        setDetection(d);
        setForm(f => ({
          ...f,
          nomAffiche: user ? `${user.prenoms || ''} ${user.nom || ''}`.trim() : '',
          smtpHost: d.smtpHost, smtpPort: d.smtpPort, smtpSecurite: d.smtpSecurite,
          imapHost: d.imapHost, imapPort: d.imapPort, imapSecurite: d.imapSecurite,
          smtpUsername: email,
          signature: defaultSignature(),
        }));
      }
    } catch { /* silencieux */ }
    setDetecting(false);
  };

  const field = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const tester = async () => {
    setTesting(true); setTestResult(null);
    try {
      const res = await axiosInstance.post('/v1/messagerie/tester', {
        smtpHost: form.smtpHost,
        smtpPort: form.smtpPort,
        smtpSecurite: form.smtpSecurite,
        username: form.smtpUsername,
        motDePasse: form.motDePasse,
      });
      setTestResult(res.data?.data);
      if (res.data?.data?.success) toast.success(t('config.connexionEtablie'));
      else toast.error(t('config.echecConnexion'));
    } catch { toast.error(t('config.erreurTest')); }
    setTesting(false);
  };

  const sauvegarder = async () => {
    setSaving(true);
    try {
      await axiosInstance.post('/v1/messagerie/config', {
        nomAffiche: form.nomAffiche,
        smtpHost: form.smtpHost, smtpPort: form.smtpPort, smtpSecurite: form.smtpSecurite,
        smtpUsername: form.smtpUsername, motDePasse: form.motDePasse,
        imapHost: form.imapHost, imapPort: form.imapPort, imapSecurite: form.imapSecurite,
        signature: form.signature,
      });
      toast.success(<><CheckCircleOutlined /> {t('config.succes')}</>);
      setStep(4);
      const res2 = await axiosInstance.get('/v1/messagerie/config');
      setConfig(res2.data?.data);
    } catch (e) {
      toast.error(e?.response?.data?.message || t('config.erreurSauvegarde'));
    }
    setSaving(false);
  };

  const sauvegarderSignature = async () => {
    try {
      await axiosInstance.patch('/v1/messagerie/signature', { signature: form.signature });
      toast.success(t('config.signatureMiseAJour'));
    } catch { toast.error(t('config.erreur')); }
  };

  if (loading) return (
    <Layout title={t('config.titreMessagerie')}>
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 32, color: T.green }} />} />
      </div>
    </Layout>
  );

  return (
    <Layout title={t('config.titrePage')}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        .msg-wizard * { font-family: 'DM Sans', sans-serif; }
        .msg-btn-primary {
          background: ${T.ink}; color: ${T.panel}; border: none;
          border-radius: 10px; height: 44px; padding: 0 24px; font-size: 14px;
          font-weight: 600; cursor: pointer; transition: all 0.2s; letter-spacing: 0.2px;
        }
        .msg-btn-primary:hover { background: ${T.green}; transform: translateY(-1px); box-shadow: 0 4px 16px rgba(10,77,46,0.25); }
        .msg-btn-primary:disabled { background: ${T.border}; color: ${T.textMut}; cursor: not-allowed; transform: none; box-shadow: none; }
        .msg-btn-ghost {
          background: transparent; color: #334155; border: 1.5px solid ${T.border};
          border-radius: 10px; height: 44px; padding: 0 20px; font-size: 14px;
          font-weight: 500; cursor: pointer; transition: all 0.2s;
        }
        .msg-btn-ghost:hover { border-color: ${T.ink}; color: ${T.ink}; }
        .msg-card { background: ${T.panel}; border: 1.5px solid ${T.border}; border-radius: 16px; }
        .msg-section-header {
          font-size: 11px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase;
          color: ${T.textMut}; margin: 0 0 16px;
        }
        .msg-mono { font-family: 'DM Mono', monospace !important; }
      `}</style>

      <div className="msg-wizard" style={{ maxWidth: 720 }}>

        {/* ── En-tête ── */}
        <div style={{ marginBottom: 32 }}>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: T.ink, margin: '0 0 6px' }}>
            {t('config.titreMessagerie')}
          </h1>
          <p style={{ color: T.textMut, fontSize: 14, margin: 0 }}>
            {config?.estConfiguree
              ? t('config.connecteEnTantQue', { email: config.emailExpediteur })
              : t('config.configurationPour', { email: user?.email })}
          </p>
        </div>

        {/* ══ ÉCRAN DE RÉCAP (config existante) ══════════════════ */}
        {step === 4 && config && (
          <RecapScreen
            config={config} form={form} field={field}
            onEdit={() => setStep(1)}
            onSaveSignature={sauvegarderSignature}
          />
        )}

        {/* ══ WIZARD ════════════════════════════════════════════= */}
        {step < 4 && (
          <>
            {/* Stepper */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 40, paddingLeft: 8 }}>
              {ETAPES.map((etape, i) => (
                <React.Fragment key={i}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                    <StepDot n={i + 1} current={step === i} done={step > i} />
                    <span style={{
                      fontSize: 11, fontWeight: step === i ? 700 : 400,
                      color: step === i ? T.ink : T.textMut, whiteSpace: 'nowrap'
                    }}>
                      {t(`config.etapes.${etape}`)}
                    </span>
                  </div>
                  {i < ETAPES.length - 1 && (
                    <div style={{
                      flex: 1, height: 2, margin: '0 6px 18px',
                      background: step > i ? T.green : T.border,
                      transition: 'background 0.4s'
                    }} />
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* ── Étape 0 : Détection ── */}
            {step === 0 && (
              <DetectionStep
                email={user?.email} detection={detection} detecting={detecting}
                onNext={() => setStep(1)} onRetry={autoDetect}
              />
            )}

            {/* ── Étape 1 : Serveurs ── */}
            {step === 1 && (
              <ServeursStep form={form} field={field}
                onBack={() => setStep(0)} onNext={() => setStep(2)} />
            )}

            {/* ── Étape 2 : Mot de passe + test ── */}
            {step === 2 && (
              <MotDePasseStep
                form={form} field={field}
                showPwd={showPwd} setShowPwd={setShowPwd}
                testing={testing} testResult={testResult}
                onTest={tester}
                onBack={() => setStep(1)}
                onNext={() => setStep(3)}
              />
            )}

            {/* ── Étape 3 : Signature ── */}
            {step === 3 && (
              <SignatureStep
                form={form} field={field}
                saving={saving}
                onBack={() => setStep(2)}
                onSave={sauvegarder}
              />
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

// ══════════════════════════════════════════════════════════════════
//  Étape 0 - Détection automatique
// ══════════════════════════════════════════════════════════════════
function DetectionStep({ email, detection, detecting, onNext, onRetry }) {
  const { t } = useTranslation('messagerie');

  return (
    <div>
      <div className="msg-card" style={{ padding: 32, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, marginBottom: 24 }}>
          {/* Icône animée */}
          <div style={{
            width: 56, height: 56, borderRadius: 16, flexShrink: 0,
            background: detecting ? T.greenSoft : (detection?.detectionReussie ? T.green : '#fef3c7'),
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26,
          }}>
            {detecting ? <LoadingOutlined style={{ color: T.green }} /> :
              detection?.detectionReussie ? <CheckCircleOutlined style={{ color: '#fff' }} /> : <SearchOutlined style={{ color: '#92400e' }} />}
          </div>
          <div>
            <h3 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: T.ink }}>
              {detecting ? t('config.detection.enCours') :
                detection?.detectionReussie ? t('config.detection.detecte', { fournisseur: detection.fournisseur }) :
                  detection ? t('config.detection.manuelle') : t('config.detection.analyse')}
            </h3>
            <p style={{ margin: 0, fontSize: 13, color: T.textMut }}>
              {detecting ? t('config.detection.analyseDomaine', { email }) :
                detection ? `@${detection.domaine}` : email}
            </p>
          </div>
        </div>

        {detection && !detecting && (
          <>
            {/* Résumé des paramètres détectés */}
            <div style={{
              background: T.contextBg, borderRadius: 12, padding: '16px 20px',
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px',
              marginBottom: 20,
            }}>
              {[
                { label: 'SMTP', value: `${detection.smtpHost}:${detection.smtpPort}`, icon: <UploadOutlined /> },
                { label: t('config.securiteSmtp'), value: detection.smtpSecurite, icon: <LockOutlined /> },
                { label: 'IMAP', value: `${detection.imapHost}:${detection.imapPort}`, icon: <DownloadOutlined /> },
                { label: t('config.securiteImap'), value: detection.imapSecurite, icon: <LockOutlined /> },
              ].map(({ label, value, icon }) => (
                <div key={label}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: T.textMut, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 2 }}>
                    {icon} {label}
                  </div>
                  <div className="msg-mono" style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>
                    {value}
                  </div>
                </div>
              ))}
            </div>

            {/* Consigne spécifique */}
            {detection.consigne && (
              <div style={{
                background: '#fff7ed', border: '1px solid #f59e0b',
                borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#92400e',
                display: 'flex', alignItems: 'flex-start', gap: 8,
              }}>
                <BulbOutlined style={{ fontSize: 14, marginTop: 1, flexShrink: 0 }} /> {detection.consigne}
              </div>
            )}
          </>
        )}

        {!detection && !detecting && (
          <div style={{ textAlign: 'center', padding: '20px 0', color: T.textMut }}>
            <MailOutlined style={{ fontSize: 32, marginBottom: 8 }} />
            <p>{t('config.detection.invite')}</p>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        <button className="msg-btn-ghost" onClick={onRetry}>
          <SyncOutlined /> {t('config.detection.relancer')}
        </button>
        <button className="msg-btn-primary" onClick={onNext}>
          {t('config.detection.suivant')}
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  Étape 1 - Serveurs SMTP / IMAP
// ══════════════════════════════════════════════════════════════════
function ServeursStep({ form, field, onBack, onNext }) {
  const { t } = useTranslation('messagerie');
  const inputS = { borderRadius: 10, border: `1.5px solid ${T.border}`, height: 42, fontSize: 14, fontFamily: "'DM Sans', sans-serif" };

  return (
    <div>
      <div className="msg-card" style={{ padding: 28, marginBottom: 20 }}>
        <p className="msg-section-header"><UploadOutlined /> {t('config.serveurs.envoi')}</p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 12, marginBottom: 16 }}>
          <FieldRow label={t('config.serveurs.hoteSmtp')}>
            <Input className="msg-mono" style={inputS} value={form.smtpHost}
              onChange={e => field('smtpHost', e.target.value)}
              placeholder="smtp.gmail.com" />
          </FieldRow>
          <FieldRow label={t('config.serveurs.port')}>
            <Input className="msg-mono" style={inputS} value={form.smtpPort}
              onChange={e => field('smtpPort', Number(e.target.value))}
              type="number" />
          </FieldRow>
        </div>

        <FieldRow label={t('config.securiteSmtp')}>
          <Select value={form.smtpSecurite} onChange={v => field('smtpSecurite', v)}
            style={{ width: '100%' }} options={PROTOCOLES}
            getPopupContainer={t => t.parentElement} />
        </FieldRow>
      </div>

      <div className="msg-card" style={{ padding: 28, marginBottom: 24 }}>
        <p className="msg-section-header"><DownloadOutlined /> {t('config.serveurs.reception')}</p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 12, marginBottom: 16 }}>
          <FieldRow label={t('config.serveurs.hoteImap')}>
            <Input className="msg-mono" style={inputS} value={form.imapHost}
              onChange={e => field('imapHost', e.target.value)}
              placeholder="imap.gmail.com" />
          </FieldRow>
          <FieldRow label={t('config.serveurs.port')}>
            <Input className="msg-mono" style={inputS} value={form.imapPort}
              onChange={e => field('imapPort', Number(e.target.value))}
              type="number" />
          </FieldRow>
        </div>

        <FieldRow label={t('config.securiteImap')}>
          <Select value={form.imapSecurite} onChange={v => field('imapSecurite', v)}
            style={{ width: '100%' }} options={PROTOCOLES}
            getPopupContainer={t => t.parentElement} />
        </FieldRow>
      </div>

      <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between' }}>
        <button className="msg-btn-ghost" onClick={onBack}>{t('config.retour')}</button>
        <button className="msg-btn-primary"
          disabled={!form.smtpHost || !form.imapHost}
          onClick={onNext}>
          {t('config.serveurs.suivant')}
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  Étape 2 - Identifiants + test de connexion
// ══════════════════════════════════════════════════════════════════
function MotDePasseStep({ form, field, showPwd, setShowPwd, testing, testResult, onTest, onBack, onNext }) {
  const { t } = useTranslation('messagerie');
  const inputS = { borderRadius: 10, border: `1.5px solid ${T.border}`, height: 42, fontSize: 14, fontFamily: "'DM Sans', sans-serif" };

  return (
    <div>
      <div className="msg-card" style={{ padding: 28, marginBottom: 20 }}>
        <p className="msg-section-header"><SafetyOutlined /> {t('config.motDePasse.identifiants')}</p>

        <FieldRow label={t('config.motDePasse.nomAffiche')} hint={t('config.motDePasse.nomAfficheAide')}>
          <Input style={inputS} value={form.nomAffiche}
            onChange={e => field('nomAffiche', e.target.value)}
            placeholder={t('config.motDePasse.nomAffichePlaceholder')} />
        </FieldRow>

        <FieldRow label={t('config.motDePasse.adresse')}>
          <Input style={{ ...inputS }} className="msg-mono" value={form.smtpUsername}
            onChange={e => field('smtpUsername', e.target.value)} />
        </FieldRow>

        <FieldRow label={t('config.motDePasse.motDePasse')}
          hint={t('config.motDePasse.motDePasseAide')}>
          <div style={{ position: 'relative' }}>
            <Input
              type={showPwd ? 'text' : 'password'}
              style={{ ...inputS, paddingRight: 44 }}
              value={form.motDePasse}
              onChange={e => field('motDePasse', e.target.value)}
              placeholder="••••••••••••"
            />
            <button
              onClick={() => setShowPwd(!showPwd)}
              style={{
                position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', color: T.textMut, padding: 0,
              }}>
              {showPwd ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            </button>
          </div>
        </FieldRow>

        {/* Bouton test */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 20 }}>
          <button className="msg-btn-ghost"
            onClick={onTest}
            disabled={!form.motDePasse || testing}
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {testing
              ? <><LoadingOutlined /> {t('config.motDePasse.testEnCours')}</>
              : <><ThunderboltOutlined /> {t('config.motDePasse.tester')}</>}
          </button>

          {testResult && (
            <div style={{ display: 'flex', gap: 8 }}>
              <StatusBadge ok={testResult.smtpOk} label={testResult.smtpOk ? 'SMTP OK' : testResult.smtpMessage} />
            </div>
          )}
        </div>
      </div>

      {/* Avertissement sécurité */}
      <div style={{
        background: T.greenSoft, borderRadius: 12, padding: '14px 18px',
        display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 24,
      }}>
        <SafetyCertificateOutlined style={{ color: T.green, fontSize: 18, flexShrink: 0, marginTop: 1 }} />
        <p style={{ margin: 0, fontSize: 13, color: '#166534', lineHeight: 1.5 }}>
          <Trans t={t} i18nKey="config.motDePasse.securite" components={{ strong: <strong /> }} />
        </p>
      </div>

      <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between' }}>
        <button className="msg-btn-ghost" onClick={onBack}>{t('config.retour')}</button>
        <button className="msg-btn-primary"
          disabled={!form.motDePasse}
          onClick={onNext}>
          {t('config.motDePasse.suivant')}
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  Étape 3 - Signature
// ══════════════════════════════════════════════════════════════════
function SignatureStep({ form, field, saving, onBack, onSave }) {
  const { t } = useTranslation('messagerie');

  return (
    <div>
      <div className="msg-card" style={{ padding: 28, marginBottom: 20 }}>
        <p className="msg-section-header"><EditOutlined /> {t('config.signature.titre')}</p>
        <p style={{ fontSize: 13, color: T.textMut, marginBottom: 16 }}>
          {t('config.signature.aide')}
        </p>

        <OssanEditor
          value={form.signature}
          onChange={html => field('signature', html)}
          placeholder={t('config.signature.placeholder')}
          minHeight={140}
        />

        {/* Prévisualisation */}
        {form.signature && (
          <div style={{ marginTop: 16 }}>
            <p style={{
              fontSize: 11, fontWeight: 600, color: T.textMut,
              textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10
            }}>
              {t('config.signature.apercu')}
            </p>
            <div style={{
              background: T.contextBg, borderRadius: 10, padding: '16px 20px',
              border: `1px solid ${T.green}`,
            }}>
              <OssanEditor readOnly value={form.signature} />
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', alignItems: 'center' }}>
        <button className="msg-btn-ghost" onClick={onBack}>{t('config.retour')}</button>
        <button className="msg-btn-primary" onClick={onSave} disabled={saving}
          style={{ minWidth: 200 }}>
          {saving ? <><LoadingOutlined /> {t('config.signature.sauvegarde')}</> : <><CheckCircleOutlined /> {t('config.signature.finaliser')}</>}
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  Écran récap - config existante
// ══════════════════════════════════════════════════════════════════
function RecapScreen({ config, form, field, onEdit, onSaveSignature }) {
  const { t } = useTranslation('messagerie');

  return (
    <div>
      {/* Carte statut */}
      <div style={{
        background: `linear-gradient(135deg, ${T.green} 0%, #145c32 100%)`,
        borderRadius: 16, padding: 28, marginBottom: 20, color: T.panel,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div>
          <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 4 }}>{t('config.recap.configuree')}</div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{config.emailExpediteur}</div>
          <div style={{ fontSize: 13, opacity: 0.8, marginTop: 4 }}>
            {config.nomAffiche}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <CheckCircleFilled style={{ fontSize: 40, opacity: 0.9 }} />
          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>{t('config.recap.active')}</div>
        </div>
      </div>

      {/* Détails serveurs */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20,
      }}>
        {[
          { title: <><UploadOutlined /> {t('config.recap.smtp')}</>, host: config.smtpHost, port: config.smtpPort, sec: config.smtpSecurite },
          { title: <><DownloadOutlined /> {t('config.recap.imap')}</>, host: config.imapHost, port: config.imapPort, sec: config.imapSecurite },
        ].map((s, i) => (
          <div key={i} className="msg-card" style={{ padding: '20px 22px' }}>
            <p style={{
              fontSize: 12, fontWeight: 700, color: T.textMut,
              textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12
            }}>
              {s.title}
            </p>
            <div className="msg-mono" style={{ fontSize: 15, fontWeight: 600, color: T.ink }}>
              {s.host}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <span style={{
                fontSize: 12, background: T.greenSoft, color: T.green,
                fontWeight: 600, padding: '2px 10px', borderRadius: 20
              }}>
                :{s.port}
              </span>
              <span style={{
                fontSize: 12, background: T.contextBg, color: T.textMut,
                fontWeight: 600, padding: '2px 10px', borderRadius: 20, border: `1px solid ${T.border}`
              }}>
                {s.sec}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Signature */}
      <div className="msg-card" style={{ padding: 24, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <p style={{
            margin: 0, fontSize: 12, fontWeight: 700, color: T.textMut,
            textTransform: 'uppercase', letterSpacing: 1
          }}><EditOutlined /> {t('config.recap.signature')}</p>
          <button className="msg-btn-ghost" onClick={onSaveSignature}
            style={{ height: 34, padding: '0 14px', fontSize: 12 }}>
            <EditOutlined /> {t('config.recap.sauvegarder')}
          </button>
        </div>
        <OssanEditor
          value={form.signature}
          onChange={html => field('signature', html)}
          placeholder={t('config.signature.placeholder')}
          minHeight={120}
        />
        {form.signature && (
          <div style={{
            marginTop: 12, background: T.contextBg, borderRadius: 8,
            padding: '12px 16px', border: `1px solid ${T.green}`
          }}>
            <OssanEditor readOnly value={form.signature} />
          </div>
        )}
      </div>

      {/* Bouton reconfigurer */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="msg-btn-ghost" onClick={onEdit}
          style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SettingOutlined /> {t('config.recap.modifier')}
        </button>
      </div>
    </div>
  );
}
